import { SortType } from "../SortType";

export const DISPLAY_TYPES = ["dropdown", "swatch", "button", "text"] as const;
export type DisplayType = (typeof DISPLAY_TYPES)[number];

export type OptionSortBy =
  | "id"
  | "name"
  | "priority"
  | "active"
  | "created_date";

/** Option (list row / summary) */
export interface OptionApi {
  id: number;
  name: string;
  slug: string | null;
  display_type: DisplayType | string;
  position: number;
  is_active: boolean;
  created_by: number | null;
  updated_by: number | null;
  created_date: string | null;
  modified_date: string | null;
}

/** Option value */
export interface OptionValueApi {
  id: number;
  option_id: number;
  value: string;
  sku_code: string | null;
  position: number;
  meta: Record<string, unknown> | null;
  is_active: boolean;
  created_by: number | null;
  updated_by: number | null;
  created_date: string | null;
  modified_date: string | null;
}

/** Option detail (includes values) */
export interface OptionDetailApi extends OptionApi {
  values: OptionValueApi[];
}

/** GET / query params */
export interface OptionListQuery {
  id?: number;
  category_id?: number;
  active?: 0 | 1;
  search?: string;
  page_no?: number;
  limit?: number;
  sort_by?: OptionSortBy;
  sort_type?: SortType;
  include_values?: boolean | 0 | 1 | "true" | "false";
}

/** GET `/:id/values` query params */
export interface OptionValueListQuery {
  search?: string;
  active?: 0 | 1;
  page_no?: number;
  limit?: number;
}

/** POST / body */
export interface CreateOptionBody {
  name: string;
  slug?: string;
  display_type?: DisplayType;
  position?: number;
  is_active?: boolean;
}

/** PATCH /:id body */
export interface UpdateOptionBody {
  name?: string;
  slug?: string;
  display_type?: DisplayType;
  position?: number;
  is_active?: boolean;
}

/** Single value create payload */
export interface CreateOptionValueBody {
  value: string;
  sku_code: string;
  position?: number;
  meta?: Record<string, unknown> | null;
  is_active?: boolean;
}

export type CreateOptionValuesBody =
  | CreateOptionValueBody
  | { values: CreateOptionValueBody[] };

/** PATCH /:id/values/:valueId */
export interface UpdateOptionValueBody {
  value?: string;
  sku_code?: string;
  position?: number;
  meta?: Record<string, unknown> | null;
  is_active?: boolean;
}

const SORT_BY_FIELDS: OptionSortBy[] = [
  "id",
  "name",
  "priority",
  "active",
  "created_date",
];

export const OPTION_SORT_BY_OPTIONS: { value: OptionSortBy; label: string }[] =
  [
    { value: "id", label: "ID" },
    { value: "name", label: "Name" },
    { value: "priority", label: "Position" },
    { value: "active", label: "Active" },
    { value: "created_date", label: "Created" },
  ];

export const DISPLAY_TYPE_OPTIONS: { value: DisplayType; label: string }[] = [
  { value: "dropdown", label: "Dropdown" },
  { value: "swatch", label: "Swatch" },
  { value: "button", label: "Button" },
  { value: "text", label: "Text" },
];

export function normalizeOptionSortBy(
  raw: string | null | undefined,
): OptionSortBy {
  if (raw && (SORT_BY_FIELDS as readonly string[]).includes(raw)) {
    return raw as OptionSortBy;
  }
  return "priority";
}

export function isDisplayType(value: string): value is DisplayType {
  return (DISPLAY_TYPES as readonly string[]).includes(value);
}

export function validateCreateOptionBody(
  body: CreateOptionBody,
): string | null {
  const name = body.name?.trim() ?? "";
  if (!name) return "Name is required.";
  if (name.length > 80) return "Name must be at most 80 characters.";
  if (body.slug != null && body.slug.trim()) {
    if (body.slug.trim().length > 100)
      return "Slug must be at most 100 characters.";
  }
  if (body.display_type != null && !isDisplayType(body.display_type)) {
    return "Invalid display type.";
  }
  if (
    body.position != null &&
    (!Number.isInteger(body.position) || body.position < 0)
  ) {
    return "Position must be an integer ≥ 0.";
  }
  return null;
}

export function validateUpdateOptionBody(
  body: UpdateOptionBody,
): string | null {
  if (
    body.name === undefined &&
    body.slug === undefined &&
    body.display_type === undefined &&
    body.position === undefined &&
    body.is_active === undefined
  ) {
    return "At least one field is required.";
  }
  if (body.name !== undefined) {
    const name = body.name.trim();
    if (!name) return "Name cannot be empty.";
    if (name.length > 80) return "Name must be at most 80 characters.";
  }
  if (body.slug != null && body.slug.trim().length > 100) {
    return "Slug must be at most 100 characters.";
  }
  if (body.display_type != null && !isDisplayType(body.display_type)) {
    return "Invalid display type.";
  }
  if (
    body.position != null &&
    (!Number.isInteger(body.position) || body.position < 0)
  ) {
    return "Position must be an integer ≥ 0.";
  }
  return null;
}

export function validateCreateOptionValueBody(
  body: CreateOptionValueBody,
  existingSkuCodes: string[] = [],
): string | null {
  const value = body.value?.trim() ?? "";
  if (!value) return "Value is required.";
  if (value.length > 120) return "Value must be at most 120 characters.";

  const sku = body.sku_code?.trim() ?? "";
  if (!sku) return "SKU code is required.";
  if (sku.length > 12) return "SKU code must be at most 12 characters.";

  const normalizedExisting = existingSkuCodes.map((s) => s.trim().toLowerCase());
  if (normalizedExisting.includes(sku.toLowerCase())) {
    return `Duplicate SKU code "${sku}".`;
  }

  if (
    body.position != null &&
    (!Number.isInteger(body.position) || body.position < 0)
  ) {
    return "Position must be an integer ≥ 0.";
  }
  return null;
}

export function validateCreateOptionValuesBatch(
  values: CreateOptionValueBody[],
  existingSkuCodes: string[] = [],
): string | null {
  if (!values.length) return "At least one value is required.";

  const seen = new Set(existingSkuCodes.map((s) => s.trim().toLowerCase()));
  for (let i = 0; i < values.length; i++) {
    const row = values[i];
    const err = validateCreateOptionValueBody(row, Array.from(seen));
    if (err) return `Row ${i + 1}: ${err}`;
    const sku = row.sku_code.trim().toLowerCase();
    if (seen.has(sku)) return `Row ${i + 1}: Duplicate SKU code "${row.sku_code}".`;
    seen.add(sku);
  }
  return null;
}

export function getApiErrorMessage(
  error: unknown,
  fallback = "Something went wrong",
): string {
  const ax = error as {
    response?: { data?: { message?: string }; status?: number };
    message?: string;
  };
  return ax?.response?.data?.message || ax?.message || fallback;
}

export function formatMetaPreview(
  meta: Record<string, unknown> | null | undefined,
): string {
  if (!meta || typeof meta !== "object") return "—";
  try {
    const json = JSON.stringify(meta);
    return json.length > 40 ? `${json.slice(0, 40)}…` : json;
  } catch {
    return "—";
  }
}

export function getMetaHex(
  meta: Record<string, unknown> | null | undefined,
): string | null {
  if (!meta || typeof meta !== "object") return null;
  const hex = meta.hex;
  if (typeof hex === "string" && hex.trim()) return hex.trim();
  return null;
}
